import apiClient, { retryRequest } from "./axios";
import { authFetch } from "./authFetch";

/**
 * 导入API - 获取导入Schema
 * @param {string} importType - 导入类型的唯一标识符
 * @returns {Promise<Object>} 导入Schema对象
 */
export const getImportSchema = async (importType) => {
    try {
        const response = await retryRequest(() => apiClient.get(`/v1/schemas/${importType}`));
        return response.data;
    } catch (error) {
        console.error("获取导入Schema失败:", error);
        throw error;
    }
};

/**
 * 导入API服务
 */
export const importApi = {
    /**
     * 获取导入Schema
     * @param {string} importType - 导入类型的唯一标识符
     * @returns {Promise<Object>} 导入Schema对象
     */
    getImportSchema: async (importType) => {
        try {
            const response = await retryRequest(() => apiClient.get(`/v1/schemas/${importType}`));
            return response.data;
        } catch (error) {
            console.error("获取导入Schema失败:", error);
            throw error;
        }
    },

    /**
     * 请求解析数据
     * @param {Object} params - 请求参数
     * @param {string} params.importType - 导入类型标识符
     * @param {File|string} params.dataContent - 文件对象或文本内容
     * @param {string} [params.parseInstruction] - 解析指导说明(可选)
     * @param {string} params.inputType - 输入类型('file'或'text')
     * @returns {Promise<Object>} 包含预览数据和预览ID的响应
     */
    requestParse: async ({ importType, dataContent, parseInstruction, inputType }) => {
        try {
            let response;

            // 文本模式: 使用标准 JSON 请求体
            if (inputType === "text") {
                // 确保 dataContent 有效 (非 undefined/null 且非空)
                const textContent = dataContent || "";
                if (!textContent.trim()) {
                    throw new Error("文本内容不能为空，请输入要解析的内容");
                }

                // 严格按照后端 TextImportPayload 模型中定义的alias来构建请求体
                const payload = {
                    importType: importType,
                    inputType: inputType,
                    dataContent: textContent,
                };

                // 仅在有解析指导时添加该字段
                if (parseInstruction && parseInstruction.trim()) {
                    payload.parseInstruction = parseInstruction.trim();
                }

                console.log("文本导入请求 payload:", payload);

                response = await apiClient.post("/v1/import/parse", payload, {
                    headers: {
                        "Content-Type": "application/json",
                    },
                });
            }
            // 文件模式: 使用 multipart/form-data 请求体
            else {
                // 确保 dataContent 是一个有效的文件对象
                if (!dataContent || !(dataContent instanceof File)) {
                    throw new Error("请选择要导入的文件");
                }

                // 构建元数据对象，严格按照 ImportMetadata 模型中定义的alias
                const metadataObj = {
                    importType: importType,
                    inputType: inputType,
                };

                // 仅在有解析指导时添加该字段
                if (parseInstruction && parseInstruction.trim()) {
                    metadataObj.parseInstruction = parseInstruction.trim();
                }

                // 转换为 JSON 字符串
                const metadataStr = JSON.stringify(metadataObj);

                // 创建 FormData
                const formData = new FormData();
                formData.append("metadata", metadataStr); // 添加元数据 JSON 字符串
                formData.append("file", dataContent); // 添加文件

                // 使用原生fetch API发送multipart/form-data请求，避免axios对FormData的处理问题
                try {
                    // 使用fetch API发送请求
                    const fetchResponse = await authFetch(`${apiClient.defaults.baseURL}/v1/import/parse`, {
                        method: "POST",
                        body: formData,
                        headers: {
                            // 不要设置Content-Type，让浏览器自动处理multipart/form-data边界
                        }
                    }, { entrypoint: "import-parse-file" });
                    
                    // 检查响应状态
                    if (!fetchResponse.ok) {
                        const errorText = await fetchResponse.text();
                        throw new Error(`请求失败 (${fetchResponse.status}): ${errorText}`);
                    }
                    
                    // 解析响应JSON
                    const responseData = await fetchResponse.json();
                    console.log("fetch API 原始响应数据:", responseData);
                    console.log("fetch API 响应数据是否包含warnings:", "warnings" in responseData);
                    response = { data: responseData };
                } catch (error) {
                    console.error("文件上传请求失败:", error);
                    // 重新抛出错误给外层catch处理
                    throw error;
                }
            }

            console.log("requestParse 最终返回数据:", response.data);
            console.log("requestParse 返回数据是否包含warnings:", "warnings" in response.data);
            return response.data;
        } catch (error) {
            console.error("解析请求失败:", error);
            throw error;
        }
    },

    /**
     * 确认导入
     * @param {string} previewId - 预览ID
     * @param {Object} [extraData] - 额外数据(可选)
     * @returns {Promise<Object>} 导入结果
     */
    confirmImport: async (previewId, extraData = {}) => {
        try {
            const response = await apiClient.post("/v1/import/confirm", {
                preview_id: previewId,
                extra_data: extraData,
            });

            return response.data;
        } catch (error) {
            console.error("确认导入失败:", error);
            throw error;
        }
    },
};

// 为了兼容旧代码，保留原来的导出方式
export const requestParse = importApi.requestParse;
export const confirmImport = importApi.confirmImport;

export default importApi;
